import { MaybeUndef } from 'types/UtilityTypes';
import { titleCase } from 'utils/formatStrings';

import { LaneTier } from '../types';

const DEFAULT_TIMEFRAME_SUFFIX = 'rates from the last 4 weeks';

type LaneHistoryStop = {
  city?: string;
  state?: string;
  zip?: string;
  marketArea?: string;
};

// formatLaneHistoryTimeframeSuffix maps API lookback strings (e.g. "84-day") to chart copy.
export function formatLaneHistoryTimeframeSuffix(timeframe?: string): string {
  if (!timeframe) {
    return DEFAULT_TIMEFRAME_SUFFIX;
  }

  const lookbackDaysRegex = /^(\d+)-day$/i;
  const match = lookbackDaysRegex.exec(timeframe);
  if (!match) {
    return DEFAULT_TIMEFRAME_SUFFIX;
  }

  const days = Number(match[1]);
  if (!Number.isFinite(days) || days <= 0) {
    return DEFAULT_TIMEFRAME_SUFFIX;
  }

  if (days % 7 === 0) {
    const weeks = days / 7;
    return weeks === 1
      ? 'rates from the last week'
      : `rates from the last ${weeks} weeks`;
  }

  return days === 1
    ? 'rates from the last day'
    : `rates from the last ${days} days`;
}

// getLaneHistoryRatesDescription is the standalone chart description for non-percentile lanes.
export function getLaneHistoryRatesDescription(timeframe?: string): string {
  const suffix = formatLaneHistoryTimeframeSuffix(timeframe);
  // Standalone charts use a sentence-style title, e.g. "84-day" -> "Rates from the last 12 weeks".
  return suffix.charAt(0).toUpperCase() + suffix.slice(1);
}

function getEndpointMarketAreas(stops: MaybeUndef<LaneHistoryStop[]>): {
  originMarketArea?: string;
  destinationMarketArea?: string;
} {
  if (!stops?.length) {
    return {};
  }

  const originMarketArea = stops[0]?.marketArea?.trim();
  const destinationMarketArea = stops[stops.length - 1]?.marketArea?.trim();

  return {
    ...(originMarketArea ? { originMarketArea } : {}),
    ...(destinationMarketArea ? { destinationMarketArea } : {}),
  };
}

function getOriginAndDestination(
  stops: MaybeUndef<LaneHistoryStop[]>
): [MaybeUndef<LaneHistoryStop>, MaybeUndef<LaneHistoryStop>] {
  if (!stops?.length) {
    return [null, null];
  }

  return [stops[0], stops[stops.length - 1]];
}

function formatCity(stop: MaybeUndef<LaneHistoryStop>): string {
  const city = stop?.city?.trim();

  if (city) {
    return titleCase(city);
  }

  return '';
}

function formatState(stop: MaybeUndef<LaneHistoryStop>): string {
  return stop?.state?.trim().toUpperCase() ?? '';
}

function format3DigitZip(stop: MaybeUndef<LaneHistoryStop>): string {
  const zip = stop?.zip?.trim().replace(/\s+/g, '');
  if (!zip) {
    return '';
  }

  return zip.slice(0, 3).toUpperCase();
}

function formatZip(stop: MaybeUndef<LaneHistoryStop>): string {
  return stop?.zip?.trim() ?? '';
}

function formatLaneEndpoints(
  origin: MaybeUndef<LaneHistoryStop>,
  destination: MaybeUndef<LaneHistoryStop>,
  formatter: (stop: MaybeUndef<LaneHistoryStop>) => string
): string {
  const originLabel = formatter(origin);
  const destinationLabel = formatter(destination);

  if (originLabel && destinationLabel) {
    return `${originLabel} to ${destinationLabel}`;
  }

  return '';
}

export function getLaneHistoryChartDescription({
  laneTier,
  stops,
  isZipCodeLookup = false,
  timeframe,
}: {
  laneTier: MaybeUndef<LaneTier>;
  stops: MaybeUndef<LaneHistoryStop[]>;
  isZipCodeLookup?: boolean;
  timeframe?: string;
}): string {
  const timeframeSuffix = formatLaneHistoryTimeframeSuffix(timeframe);
  const { originMarketArea, destinationMarketArea } =
    getEndpointMarketAreas(stops);
  const [origin, destination] = getOriginAndDestination(stops);

  switch (laneTier) {
    case LaneTier.StateLaneTier: {
      const lane = formatLaneEndpoints(origin, destination, formatState);
      return lane ? `${lane} ${timeframeSuffix}` : '';
    }
    case LaneTier.CityStateLaneTier: {
      const lane = formatLaneEndpoints(origin, destination, formatCity);
      return lane ? `${lane} ${timeframeSuffix}` : '';
    }
    case LaneTier._3DigitZipLaneTier: {
      const lane = formatLaneEndpoints(origin, destination, format3DigitZip);
      return lane ? `${lane} ${timeframeSuffix}` : '';
    }
    case LaneTier.MarketLaneTier: {
      if (originMarketArea && destinationMarketArea) {
        return `${originMarketArea} to ${destinationMarketArea} ${timeframeSuffix}`;
      }

      return `Market-to-market ${timeframeSuffix}`;
    }
    default: {
      const formatter = isZipCodeLookup ? formatZip : formatCity;
      const lane = formatLaneEndpoints(origin, destination, formatter);
      if (lane) {
        return `${lane} ${timeframeSuffix}`;
      }

      const stateLane = formatLaneEndpoints(origin, destination, formatState);
      return stateLane ? `${stateLane} ${timeframeSuffix}` : '';
    }
  }
}
